/**
 * My vehicles — plate cards with parking sticker + add-vehicle sheet.
 * Wired: GET/POST /api/services/vehicles (409 duplicate plate → inline error).
 * Port of design/source/screens-services.jsx Vehicles.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { fonts, space } from '@bero/tokens';
import { str, useGateList } from '@/components/gate/api';
import { IconTile, InfoNote, Segmented } from '@/components/gate/bits';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import {
  AppText, Button, Card, EmptyState, Field, Hairline, IconButton, Input,
  Pill, Screen, Sheet, Skeleton, TopBar,
} from '@/ui';

const TYPES = ['Car', 'Two-wheeler', 'Other'];
const TYPE_TO_API: Record<string, string> = { Car: 'CAR', 'Two-wheeler': 'TWO_WHEELER', Other: 'OTHER' };
// Vehicle-type display keys — resolved at render so they localise.
const VEH_TYPE_KEY: Record<string, DictKey> = { CAR: 'veh_car', TWO_WHEELER: 'veh_two_wheeler', OTHER: 'veh_other' };

type VehicleRow = { id: string; plate: string; model: string; apiType: string; sticker: string };

function toRow(v: unknown, i: number): VehicleRow {
  const ty = (str(v, 'type', 'vehicleType') ?? 'CAR').toUpperCase();
  return {
    id: str(v, 'id') ?? `V-${i}`,
    plate: str(v, 'plate', 'number') ?? '—',
    model: str(v, 'model', 'desc') ?? '',
    apiType: VEH_TYPE_KEY[ty] ? ty : 'CAR',
    sticker: str(v, 'stickerNo', 'sticker') ?? '—',
  };
}

export default function VehiclesScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, setItems, state } = useGateList<unknown>('/api/services/vehicles', 'vehicles');
  const rows = items.map(toRow);

  const [adding, setAdding] = React.useState(false);
  const [plate, setPlate] = React.useState('');
  const [type, setType] = React.useState('Car');
  const [model, setModel] = React.useState('');
  const [sticker, setSticker] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  const openAdd = () => {
    setPlate(''); setType('Car'); setModel(''); setSticker('');
    setFormError(null);
    setAdding(true);
  };

  const submit = async () => {
    const p = plate.trim().toUpperCase();
    if (!p) { setFormError(t('veh_plate_error')); return; }
    setSaving(true);
    setFormError(null);
    try {
      const body = await api.post<unknown>('/api/services/vehicles', {
        plate: p,
        type: TYPE_TO_API[type] ?? 'CAR',
        model: model.trim(),
        stickerNo: sticker.trim() || null,
      });
      const vehicle = (body && typeof body === 'object' && 'vehicle' in body)
        ? (body as { vehicle: unknown }).vehicle
        : body;
      setItems([...items, vehicle]);
      setAdding(false);
    } catch (e) {
      const status = (e as { status?: number }).status;
      setFormError(
        status === 409
          ? t('veh_dup_error')
          : (e as { error?: string }).error ?? t('veh_add_error'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('veh_title')}
        onBack={() => router.back()}
        right={
          <IconButton label={t('veh_add')} onPress={openAdd}>
            <Icon name="plus" size={22} color={colors.textPrimary} />
          </IconButton>
        }
      />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1].map((i) => <Skeleton key={i} h={116} />)}
          </View>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="car" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('veh_err_title') : t('veh_empty_title')}
            body={state === 'error' ? t('veh_err_body') : t('veh_empty_body')}
            cta={state === 'error' ? undefined : t('veh_add_cta')}
            onCta={openAdd}
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {rows.map((v) => (
              <Card key={v.id}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.s2 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, flex: 1, minWidth: 0 }}>
                    <IconTile name="car" size={44} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText mono weight="500" style={{ fontSize: 16, lineHeight: 22, letterSpacing: 0.96, fontFamily: fonts.monoMedium }}>
                        {v.plate}
                      </AppText>
                      {v.model ? (
                        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{v.model}</AppText>
                      ) : null}
                    </View>
                  </View>
                  <Pill status="approved" label={t(VEH_TYPE_KEY[v.apiType] ?? 'veh_car').toUpperCase()} />
                </View>
                <Hairline />
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: space.s3 }}>
                  <AppText variant="bodySm" tone="tertiary">{t('veh_sticker')}</AppText>
                  <AppText mono weight="500" variant="bodyMd">{v.sticker}</AppText>
                </View>
              </Card>
            ))}
          </View>
        )}
      </Screen>

      <Sheet visible={adding} onClose={() => setAdding(false)}>
        <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s4 }}>{t('veh_add')}</AppText>
        <View style={{ gap: space.s4 }}>
          <Field label={t('veh_plate')}>
            <Input
              placeholder="MH 01 AB 1234"
              value={plate}
              onChangeText={(val) => setPlate(val.toUpperCase())}
              autoCapitalize="characters"
              hasError={!!formError}
            />
          </Field>
          <Field label={t('veh_type')}>
            <Segmented options={TYPES} labels={TYPES.map((x) => t(VEH_TYPE_KEY[TYPE_TO_API[x]] ?? 'veh_car'))} value={type} onChange={setType} />
          </Field>
          <Field label={t('veh_model')}>
            <Input placeholder={t('veh_model_ph')} value={model} onChangeText={setModel} />
          </Field>
          <Field label={t('veh_sticker_field')}>
            <Input placeholder={t('veh_sticker_ph')} value={sticker} onChangeText={setSticker} />
          </Field>
          {formError ? <InfoNote icon="info" tint={colors.danger500}>{formError}</InfoNote> : null}
          <Button title={t('veh_add')} loading={saving} onPress={submit} />
        </View>
      </Sheet>
    </Screen>
  );
}
